import React, { useState } from "react";
import { ChevronDown } from "react-feather";

// Collapsible section card for the Enquiry / Order / Quotation detail panes -
// too many dense sub-sections (Pricing, Guard Requirements, Installation,
// Quotation...) to show fully expanded at once in a split-view detail pane.
//
// Styled by whether the panel actually HOLDS anything (2026-09-12, by
// instruction: "all that linked collapsible panels look plain - make it
// colour changed if the panels contain data"):
//   - hasData (default)  -> its own accent: coloured left edge, tinted header,
//                           icon in a filled circle, accent title, optional
//                           count pill + one-line summary in the header.
//   - hasData === false  -> muted grey with a "No data yet" tag, and starts
//                           collapsed so empty sections stop taking room.
// Accent colours follow DeploymentContextCards' palette so the app reads as
// one design: green money, blue links, red guards, purple work, maroon docs.
export const PANEL_ACCENT = {
  money: "#1E8449",
  link: "#2E86AB",
  guards: "#C0392B",
  work: "#8E44AD",
  document: "#6E1423",
  services: "#E67E22",
  neutral: "#546E7A",
};

const EMPTY_COLOR = "#98A2B3";

const rgba = (hex, alpha) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// `forceOpen` keeps the body visible while something inside it needs to be
// seen - e.g. the Update form of an empty (so collapsed) Installation panel.
const CollapsibleCard = ({
  title, icon: Icon, defaultOpen, actions, children,
  hasData, accent = PANEL_ACCENT.document, count, summary, forceOpen = false,
}) => {
  const filled = hasData !== false;
  const [openState, setOpen] = useState(defaultOpen ?? filled);
  const open = openState || forceOpen;
  const [hover, setHover] = useState(false);
  const color = filled ? accent : EMPTY_COLOR;

  return (
    <div
      className="card mb-3"
      style={{
        border: `1px solid ${rgba(color, filled ? 0.22 : 0.3)}`,
        borderLeft: `4px solid ${color}`,
        boxShadow: filled ? `0 2px 10px ${rgba(accent, 0.08)}` : "none",
        overflow: "hidden",
      }}
    >
      <div
        className="card-header d-flex align-items-center justify-content-between gap-2"
        style={{
          cursor: "pointer",
          background: filled
            ? `linear-gradient(90deg, ${rgba(accent, hover ? 0.16 : 0.1)}, ${rgba(accent, 0.02)} 75%)`
            : hover ? "#F2F4F7" : "#F9FAFB",
          borderBottom: open ? `1px solid ${rgba(color, 0.18)}` : "none",
          transition: "background 0.15s",
        }}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <span className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <ChevronDown
            size={16}
            style={{ color, flexShrink: 0, transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}
          />
          {Icon && (
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 28, height: 28, flexShrink: 0,
                background: filled ? color : "#EEF0F3",
                color: filled ? "#fff" : EMPTY_COLOR,
                boxShadow: filled ? `0 2px 6px ${rgba(accent, 0.35)}` : "none",
              }}
            >
              <Icon size={14} />
            </span>
          )}
          <span className="fw-semibold text-nowrap" style={{ color: filled ? accent : "#667085" }}>{title}</span>
          {filled && count != null && (
            <span className="badge rounded-pill" style={{ background: accent, color: "#fff", fontWeight: 600 }}>{count}</span>
          )}
          {!filled && (
            <span className="badge rounded-pill" style={{ background: "#EEF0F3", color: "#667085", fontWeight: 500 }}>
              No data yet
            </span>
          )}
          {filled && summary && (
            <span className="small text-truncate" style={{ color: rgba(accent, 0.85), fontWeight: 600 }}>{summary}</span>
          )}
        </span>
        {actions && (
          <span onClick={(e) => e.stopPropagation()} className="d-flex gap-2 flex-shrink-0">
            {actions}
          </span>
        )}
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
};

export default CollapsibleCard;
